import { useQuery } from 'react-query';

import type { AccountStatusViewDto } from '@identity/application/dtos';
import { getAccountUseCases } from '@identity/composition';
import { accountStatusKey } from '@identity/presentation/constants';
import { useIdentityFlow } from '@identity/presentation/context';
import { useIdentityUserId } from '@identity/presentation/hooks/useIdentityUserId';

export type UseAccountStatusParams = {
  /**
   * Whether to ask at all. Every wizard screen needs the status on mount, so it defaults to `true`.
   * The gate passes `false`: it runs over the whole application, and asking from a public route
   * would be a `401` on every visit of a logged-out user.
   */
  enabled?: boolean;
};

/**
 * Reads the account status that drives the wizard and the gate.
 *
 * It does not refetch on focus: the polling already keeps the flow current with a controlled
 * interval, and on the last screen a focus refetch would run after the cookie was deleted.
 *
 * The gate and the wizard share one cache key, so entering the wizard reuses the entry the gate
 * already brought.
 */
export const useAccountStatus = ({ enabled = true }: UseAccountStatusParams = {}) => {
  const flow = useIdentityFlow();
  const userId = useIdentityUserId();
  const { getAccountStatus } = getAccountUseCases(flow);

  return useQuery<AccountStatusViewDto, Error>(accountStatusKey(userId), () => getAccountStatus.execute(), {
    enabled,
    refetchOnWindowFocus: false,
  });
};
